import { useState, useEffect, useCallback, useRef } from "react";
import { Plus, Pencil, Trash2, Loader2, Package, GripVertical, Percent, Save, Star, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { api, formatBRL } from "@/lib/api";
import { CATEGORIES } from "@/data/content";

const EMPTY = { name: "", desc: "", category: "mei", amount: "", active: true, featured: false, sort: 0 };

export default function ServicesManager() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // service obj or null
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [discount, setDiscount] = useState("10");
  const [savingDiscount, setSavingDiscount] = useState(false);
  const [pixKey, setPixKey] = useState("");
  const [pixName, setPixName] = useState("");
  const [pixExpiration, setPixExpiration] = useState("15");
  const [savingPix, setSavingPix] = useState(false);
  const dragId = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/services");
      setServices(data);
    } catch {
      toast.error("Erro ao carregar serviços");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api.get("/settings").then(({ data }) => {
      setDiscount(String(data.pix_discount_percent));
      setPixKey(data.pix_key || "");
      setPixName(data.pix_merchant_name || "");
      setPixExpiration(String(data.pix_expiration_minutes ?? 15));
    }).catch(() => {});
  }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...s, amount: String(s.amount) }); setOpen(true); };

  const save = async () => {
    if (!form.name || !form.amount) { toast.error("Preencha nome e valor"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, desc: form.desc, category: form.category, amount: parseFloat(String(form.amount).replace(",", ".")), active: form.active, featured: !!form.featured, sort: Number(form.sort) || 0 };
      if (editing) await api.put(`/admin/services/${editing.id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success(editing ? "Serviço atualizado" : "Serviço criado");
      setOpen(false);
      load();
    } catch {
      toast.error("Erro ao salvar serviço");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/services/${deleteId}`);
      toast.success("Serviço removido");
      setDeleteId(null);
      load();
    } catch {
      toast.error("Erro ao remover");
    }
  };

  const saveDiscount = async () => {
    setSavingDiscount(true);
    try {
      const { data } = await api.put("/admin/settings", { pix_discount_percent: parseFloat(String(discount).replace(",", ".")) || 0 });
      setDiscount(String(data.pix_discount_percent));
      toast.success("Desconto do Pix atualizado");
    } catch {
      toast.error("Erro ao salvar desconto");
    } finally {
      setSavingDiscount(false);
    }
  };

  const savePixReceiver = async () => {
    setSavingPix(true);
    try {
      const { data } = await api.put("/admin/settings", {
        pix_key: pixKey,
        pix_merchant_name: pixName,
        pix_expiration_minutes: parseInt(String(pixExpiration), 10) || 15,
      });
      setPixExpiration(String(data.pix_expiration_minutes ?? 15));
      toast.success("Dados de recebimento Pix atualizados");
    } catch {
      toast.error("Erro ao salvar dados do Pix");
    } finally {
      setSavingPix(false);
    }
  };

  const toggleFeatured = async (s) => {    try {
      await api.put(`/admin/services/${s.id}`, { name: s.name, desc: s.desc, category: s.category, amount: s.amount, active: s.active, sort: s.sort, featured: !s.featured });
      setServices((prev) => prev.map((x) => (x.id === s.id ? { ...x, featured: !s.featured } : x)));
      toast.success(!s.featured ? "Marcado como mais procurado" : "Removido dos mais procurados");
    } catch {
      toast.error("Erro ao atualizar");
    }
  };

  const onDrop = (targetId, category) => {    const src = dragId.current;
    dragId.current = null;
    if (!src || src === targetId) return;
    const srcSvc = services.find((s) => s.id === src);
    if (!srcSvc || srcSvc.category !== category) return;
    const group = services.filter((s) => s.category === category);
    const fromIdx = group.findIndex((s) => s.id === src);
    const toIdx = group.findIndex((s) => s.id === targetId);
    group.splice(toIdx, 0, group.splice(fromIdx, 1)[0]);
    const order = [];
    Object.keys(CATEGORIES).forEach((cat) => {
      (cat === category ? group : services.filter((s) => s.category === cat)).forEach((s) => order.push(s));
    });
    setServices(order);
    api.post("/admin/services/reorder", { ids: order.map((s) => s.id) }).catch(() => toast.error("Erro ao reordenar"));
  };

  if (loading) return <div className="py-20 grid place-items-center"><Loader2 className="h-7 w-7 animate-spin text-slate-400" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading font-bold text-2xl text-slate-900">Gerenciar serviços</h1>
        <Button data-testid="service-new-button" onClick={openNew} className="bg-[#0F294A] hover:bg-[#1E3A8A] text-white rounded-full gap-1.5"><Plus className="h-4 w-4" /> Novo serviço</Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="discount-settings">
        <span className="grid place-items-center h-11 w-11 rounded-xl bg-emerald-100 text-emerald-600 shrink-0"><Percent className="h-5 w-5" /></span>
        <div className="flex-1">
          <p className="font-heading font-bold text-slate-900">Desconto no Pix</p>
          <p className="text-sm text-slate-500">Percentual aplicado automaticamente quando o cliente paga via Pix.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-28">
            <Input data-testid="discount-input" value={discount} onChange={(e) => setDiscount(e.target.value)} className="pr-7 text-right" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
          </div>
          <Button data-testid="discount-save" onClick={saveDiscount} disabled={savingDiscount} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-1.5">
            {savingDiscount ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvar
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6" data-testid="pix-receiver-settings">
        <div className="flex items-center gap-3 mb-4">
          <span className="grid place-items-center h-11 w-11 rounded-xl bg-emerald-100 text-emerald-600 shrink-0"><QrCode className="h-5 w-5" /></span>
          <div>
            <p className="font-heading font-bold text-slate-900">Recebimento Pix</p>
            <p className="text-sm text-slate-500">Chave Pix e nome do recebedor usados no QR Code gerado para os clientes.</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Chave Pix (celular, e-mail, CPF/CNPJ ou aleatória)</Label>
            <Input data-testid="pix-key-input" value={pixKey} onChange={(e) => setPixKey(e.target.value)} placeholder="+5521983483905" className="mt-1.5" />
          </div>
          <div>
            <Label>Nome do recebedor</Label>
            <Input data-testid="pix-name-input" value={pixName} onChange={(e) => setPixName(e.target.value)} placeholder="CENTRAL SERV MEI" className="mt-1.5" />
          </div>
          <div>
            <Label>Tempo de expiração do QR Code (minutos)</Label>
            <Input data-testid="pix-expiration-input" type="number" min="5" max="60" value={pixExpiration} onChange={(e) => setPixExpiration(e.target.value)} placeholder="15" className="mt-1.5" />
            <p className="text-xs text-slate-400 mt-1">Entre 5 e 60 minutos. Um lembrete por e-mail é enviado pouco antes de expirar.</p>
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <Button data-testid="pix-receiver-save" onClick={savePixReceiver} disabled={savingPix} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-1.5">
            {savingPix ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvar
          </Button>
        </div>
      </div>

      <p className="text-xs text-slate-400 mb-6">Dica: arraste os serviços pelo ícone para alterar a ordem exibida no site.</p>

      {Object.entries(CATEGORIES).map(([key, cat]) => {
        const items = services.filter((s) => s.category === key);
        return (
          <div key={key} className="mb-8" data-testid={`admin-service-group-${key}`}>
            <div className="flex items-center gap-2 mb-3">
              <span className="grid place-items-center h-8 w-8 rounded-lg text-white" style={{ backgroundColor: cat.color }}><cat.icon className="h-4 w-4" /></span>
              <h3 className="font-heading font-bold text-slate-900">{cat.label}</h3>
              <span className="text-xs text-slate-400">({items.length})</span>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
              {items.length === 0 && <p className="px-4 py-6 text-sm text-slate-400 text-center">Nenhum serviço nesta categoria.</p>}
              {items.map((s) => (
                <div
                  key={s.id}
                  data-testid={`admin-service-${s.id}`}
                  draggable
                  onDragStart={() => (dragId.current = s.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(s.id, key)}
                  className="flex items-center gap-3 px-4 py-3 cursor-move hover:bg-slate-50"
                >
                  <GripVertical className="h-4 w-4 text-slate-300 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate flex items-center gap-2">
                      {s.name}
                      {!s.active && <span className="text-[10px] font-bold uppercase bg-slate-200 text-slate-500 rounded px-1.5 py-0.5">Inativo</span>}
                    </p>
                    <p className="text-xs text-slate-400 truncate">{s.desc}</p>
                  </div>
                  <span className="font-bold text-slate-900 shrink-0">{formatBRL(s.amount)}</span>
                  <Button data-testid={`service-feature-${s.id}`} size="icon" variant="ghost" onClick={() => toggleFeatured(s)} title="Marcar como mais procurado" className={s.featured ? "text-amber-500" : "text-slate-300 hover:text-amber-500"}><Star className={`h-4 w-4 ${s.featured ? "fill-amber-400" : ""}`} /></Button>
                  <Button data-testid={`service-edit-${s.id}`} size="icon" variant="ghost" onClick={() => openEdit(s)} className="text-slate-500 hover:text-[#0F294A]"><Pencil className="h-4 w-4" /></Button>
                  <Button data-testid={`service-delete-${s.id}`} size="icon" variant="ghost" onClick={() => setDeleteId(s.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="service-form-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar serviço" : "Novo serviço"}</DialogTitle>
          <DialogDescription>Defina o nome, categoria, valor e descrição do serviço exibido no site.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="s-name">Nome do serviço</Label>
              <Input id="s-name" data-testid="service-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="s-desc">Descrição</Label>
              <Textarea id="s-desc" data-testid="service-input-desc" value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} className="mt-1.5 min-h-[80px]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="service-input-category" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="s-amount">Valor (R$)</Label>
                <Input id="s-amount" data-testid="service-input-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="89.90" className="mt-1.5" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
              <div>
                <p className="font-medium text-slate-900 text-sm">Serviço ativo</p>
                <p className="text-xs text-slate-400">Visível no site para os clientes</p>
              </div>
              <Switch data-testid="service-input-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50/50 p-3">
              <div>
                <p className="font-medium text-slate-900 text-sm">Mais procurado</p>
                <p className="text-xs text-slate-400">Destaca o serviço no topo do site</p>
              </div>
              <Switch data-testid="service-input-featured" checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancelar</Button>
            <Button data-testid="service-save-button" onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover serviço?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita. O serviço deixará de aparecer no site.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="service-confirm-delete" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
